import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import VisitorAnalytics from '@/components/admin/VisitorAnalytics';
import { DollarSign, Package, ShoppingCart, Star, TrendingUp, Users, Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testingTelegram, setTestingTelegram] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const testTelegramNotification = async () => {
    setTestingTelegram(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/test-telegram`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notificação de teste enviada! Verifique seu Telegram.');
    } catch (error) {
      console.error('Telegram test error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar notificação de teste');
    } finally {
      setTestingTelegram(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="h-16 w-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      label: 'Receita Total',
      value: `R$ ${stats?.total_revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'text-green-500'
    },
    {
      label: 'Total de Pedidos',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'text-blue-500'
    },
    {
      label: 'Produtos',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-purple-500'
    },
    {
      label: 'Avaliações',
      value: stats?.total_reviews || 0,
      icon: Star,
      color: 'text-yellow-500'
    },
    {
      label: 'Pedidos Pendentes',
      value: stats?.pending_orders || 0,
      icon: TrendingUp,
      color: 'text-orange-500'
    },
    {
      label: 'Ticket Médio',
      value: `R$ ${stats?.average_order_value?.toFixed(2) || '0.00'}`,
      icon: Users,
      color: 'text-primary'
    }
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8 gap-4">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Dashboard</h1>
          
          {/* Telegram Test Button */}
          <button
            onClick={testTelegramNotification}
            disabled={testingTelegram}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            data-testid="test-telegram-btn"
          >
            {testingTelegram ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Bell className="h-4 w-4" />
            )}
            Testar Notificação Telegram
          </button>
        </div>

        {/* Visitor Analytics Section */}
        <VisitorAnalytics />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-zinc-900/50 border border-white/10 p-3 sm:p-4 lg:p-6"
                data-testid={`stat-card-${index}`}
              >
                <div className="flex items-center justify-between mb-2 lg:mb-4">
                  <span className="text-zinc-400 text-xs lg:text-sm uppercase tracking-wider">{stat.label}</span>
                  <Icon className={`h-4 w-4 lg:h-6 lg:w-6 ${stat.color}`} />
                </div>
                <p className="font-heading text-lg sm:text-xl lg:text-3xl font-bold text-white">{stat.value}</p>
              </div>
            );
          })}
        </div>

        {/* Revenue Chart */}
        <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6 mb-6 lg:mb-8">
          <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6">Receita dos Últimos 7 Dias</h2>
          <div className="h-40 lg:h-64 flex items-end gap-1 sm:gap-2">
            {stats?.revenue_by_day?.map((day, index) => {
              const maxRevenue = Math.max(...(stats.revenue_by_day.map(d => d.revenue) || [1]));
              const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-primary hover:bg-primary/80 transition-colors cursor-pointer relative group"
                    style={{ height: `${height}%`, minHeight: day.revenue > 0 ? '4px' : '0' }}
                  >
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      R$ {day.revenue.toFixed(2)}
                    </div>
                  </div>
                  <span className="text-[10px] lg:text-xs text-zinc-500 mt-1 lg:mt-2">
                    {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
          {/* Top Products */}
          <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
            <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6">Produtos Mais Vendidos</h2>
            {stats?.top_products?.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {stats.top_products.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 lg:gap-4 pb-3 lg:pb-4 border-b border-white/5 last:border-0">
                    <img 
                      src={item.product.image} 
                      alt={item.product.name}
                      className="w-12 h-12 lg:w-16 lg:h-16 object-cover border border-white/10"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-xs lg:text-sm font-bold text-white truncate">{item.product.name}</p>
                      <p className="text-[10px] lg:text-xs text-zinc-400">{item.sales_count} vendidos</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs lg:text-sm text-primary font-bold">R$ {item.revenue.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-center py-6 lg:py-8 text-sm">Nenhuma venda ainda</p>
            )}
          </div>

          {/* Recent Orders */}
          <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
            <h2 className="font-heading text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6">Pedidos Recentes</h2>
            {stats?.recent_orders?.length > 0 ? (
              <div className="space-y-3 lg:space-y-4">
                {stats.recent_orders.slice(0, 5).map((order, index) => (
                  <div key={index} className="pb-3 lg:pb-4 border-b border-white/5 last:border-0">
                    <div className="flex justify-between items-start mb-1 lg:mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[10px] lg:text-xs text-zinc-400">#{order.id.slice(0, 8)}</p>
                        <p className="text-xs lg:text-sm text-white truncate">{order.customer_name}</p>
                      </div>
                      <span className={`text-[10px] lg:text-xs px-2 py-1 rounded ml-2 whitespace-nowrap ${
                        order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                        order.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {order.status === 'pending' ? 'Pendente' : order.status === 'completed' ? 'Completo' : order.status}
                      </span>
                    </div>
                    <p className="font-mono text-xs lg:text-sm text-primary font-bold">R$ {order.total.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-center py-6 lg:py-8 text-sm">Nenhum pedido ainda</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;